import { ComponentProps, useState } from "react";
import { PASSWORD_HINT, passwordError } from "../../util/password";
import { faEye, faEyeSlash } from "@fortawesome/free-regular-svg-icons";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { InputField } from "../../design-system/input-field";
import { SignupStepFrame } from "../SignupStepFrame";
import { SignupStepProps } from "../signupForm";

/** A password box with a show/hide button, like the login page's. */
const PasswordField = (
  props: Omit<
    ComponentProps<typeof InputField>,
    "type" | "trailing" | "autoComplete"
  >,
) => {
  const [shown, setShown] = useState(false);
  return (
    <InputField
      {...props}
      type={shown ? "text" : "password"}
      autoComplete="new-password"
      trailing={
        <button
          type="button"
          aria-label={shown ? "Hide password" : "Show password"}
          onClick={() => setShown(!shown)}
          className="tw-flex tw-h-[16px] tw-w-[16px] tw-items-center tw-justify-center tw-border-none tw-bg-transparent tw-p-0 tw-text-verse-icon-primary"
        >
          <FontAwesomeIcon icon={shown ? faEyeSlash : faEye} />
        </button>
      }
    />
  );
};

/** Step 2: the password for the clinician's new login, entered twice. Next
 * submits the sign-up. */
export const PasswordStep = ({
  form,
  updateForm,
  onBack,
  onNext,
  serverErrors = {},
  submitting = false,
  submitError = null,
}: SignupStepProps) => {
  const confirm = form.passwordConfirm;
  const [touched, setTouched] = useState({ password: false, confirm: false });
  // E.g. Auth0's rejection at submit; cleared when the password is edited.
  const rejection = serverErrors.password ?? null;

  const ruleError = passwordError(form.password);
  const matches = confirm === form.password;

  return (
    <SignupStepFrame
      step="password"
      title="Set a password"
      eyebrow={form.email}
      narrow
      onBack={onBack}
      onNext={onNext}
      nextLabel={submitting ? "Creating your account…" : undefined}
      nextDisabled={!!ruleError || !matches || !!rejection || submitting}
      error={submitError}
    >
      <div className="tw-mt-[24px] tw-flex tw-flex-col tw-gap-[16px]">
        <PasswordField
          id="signup-password"
          label="Password"
          hint={PASSWORD_HINT}
          value={form.password}
          onChange={(password) => updateForm({ password })}
          onBlur={() => setTouched((prev) => ({ ...prev, password: true }))}
          error={rejection ?? (touched.password ? ruleError : null)}
        />
        <PasswordField
          id="signup-password-confirm"
          label="Re-enter your password"
          value={confirm}
          onChange={(passwordConfirm) => updateForm({ passwordConfirm })}
          onBlur={() => setTouched((prev) => ({ ...prev, confirm: true }))}
          error={
            touched.confirm && confirm && !matches
              ? "Passwords don't match"
              : null
          }
        />
      </div>
    </SignupStepFrame>
  );
};
